import { useDataContainer } from "@elsa-zku/lib/helpers/useDataContainer";
import { action, map, StoreValue } from "nanostores";
import { AllKeys, useStore, UseStoreOptions } from "@nanostores/react";
import React from "react";
import { getAppApartmentsTypesByBuilding, getAppBuildingShowList } from "src/api/building";
import { APARTMENT_TYPE } from "src/dictionaries/apartmentTypes";
import { IBuildingShort } from "src/interfaces/models";
import { tariffInput } from "src/routes/CalculationAdd/helpers/calculationAddFields";
import { userStore } from "src/store/user";

export type StoreCompany = {
  id: number,
  title: string,
  groups: string[],
};

export type StoreGroup = {
  title: string,
  city: string,
  is_archived: boolean,
  buildings: number[],
  company_name: string
};

export interface BuildingsStore {
  selected: number,
  companiesList: number[],
  companiesDict: Record<string, StoreCompany>,
  groupsList: string[],
  groupsDict: Record<string, StoreGroup>,
  buildingsList: number[],
  buildingsDict: Record<string, IBuildingShort>
}

export type UseBuildingOptions = UseStoreOptions<typeof buildingsStore>;

export const buildingsStore = map<BuildingsStore>({
  companiesList: null,
  companiesDict: null,
  selected:      null,
  groupsList:    null,
  groupsDict:    null,
  buildingsDict: null,
  buildingsList: null,
});

let _buildingsLoaded = false;
export const loadBuildings = (force?: boolean) => {
  if (!force && _buildingsLoaded) return;
  _buildingsLoaded = true;
  return getAppBuildingShowList().then((buildings) => {
    const companiesList: number[] = [];
    const companiesDict: Record<string, StoreCompany> = {};
    const groupsList: string[] = [];
    const groupsDict: Record<string, StoreGroup> = {};
    const buildingsList: number[] = [];
    const buildingsDict: Record<string, IBuildingShort> = {};

    let prevBuilding: IBuildingShort;
    buildings.forEach(building => {
      const { id, residential_complex, city, is_archived, company_id, company_name } = building;
      if (!companiesDict[company_id]) {
        companiesDict[company_id] = {
          id:     company_id,
          title:  company_name,
          groups: [],
        }
        companiesList.push(company_id);
      }
      const groupName = company_id + '-' + (residential_complex ? residential_complex.toLowerCase() + (is_archived ? '-archived' : '') : 'NON_COMPLEX');
      if (!groupsDict[groupName]) {
        groupsDict[groupName] = {
          title:        residential_complex || '',
          city,
          is_archived,
          company_name: company_name,
          buildings:    []
        };
        companiesDict[company_id].groups.push(groupName);
        groupsList.push(groupName);
      }
      const group = groupsDict[groupName];
      group.buildings.push(id);
      buildingsList.push(id);
      buildingsDict[id] = building;
      prevBuilding = building;
    });

    let selected = +localStorage.selected_building;
    if (!buildingsDict[selected]) selected = buildingsList[0];

    buildingsStore.set({
      companiesDict,
      companiesList,
      groupsDict,
      groupsList,
      buildingsDict,
      buildingsList,
      selected,
    });
  }, () => {});
}

export const selectBuilding = action(buildingsStore, 'select', (store, id: number) => {
  if (!id) {
    const { buildingsList } = store.get();
    if (buildingsList) id = buildingsList[0];
  }
  if (!id) return;
  store.setKey('selected', id);
  localStorage.selected_building = id;
})

export const useBuildings = () => {
  return useStore(buildingsStore, selectedBuildingIdConfig);
}

const selectedBuildingIdConfig: UseBuildingOptions = { keys: ['selected'] }
export const useSelectedBuildingId = () => {
  const { selected } = useStore(buildingsStore, selectedBuildingIdConfig);
  return selected;
}

const selectedBuildingConfig: UseBuildingOptions = { keys: ['selected', "buildingsDict"] }
export const useSelectedBuilding = () => {
  const { selected, buildingsDict } = useStore(buildingsStore, selectedBuildingConfig);
  return selected ? buildingsDict[selected] : null;
}

export const useSelectedBuildingOnlyForReports = () => {
  const { selected, buildingsDict } = useStore(buildingsStore, selectedBuildingConfig);
  if (!selected) return false;
  const building = buildingsDict[selected]
  return building.only_for_reports;
}

const buildingConfig: UseBuildingOptions = { keys: ["buildingsDict"] }
export const useBuilding = (buildingId: number) => {
  const { buildingsDict } = useStore(buildingsStore, buildingConfig);
  return buildingsDict[buildingId];
}

export const useIsAnnualCalculation = (buildingId?: number) => {
  const { selected, buildingsDict } = useStore(buildingsStore, selectedBuildingConfig);
  if (!buildingsDict) return null;
  if (!buildingId) buildingId = selected;
  const building = buildingsDict[buildingId];
  if (!building) return null;
  return building.is_annual_calculation;
}

const getSelectedCompanyId = (store: BuildingsStore) => {
  const { selected, buildingsDict } = store;
  const building = buildingsDict[selected];
  return building ? building.company_id : null;
}

export const useSelectedCompanyId = () => {
  const [companyId, setCompanyId] = React.useState<number>(getSelectedCompanyId(buildingsStore.get()));
  React.useEffect(() => {
    return buildingsStore.subscribe(store => {
      setCompanyId(getSelectedCompanyId(store));
    });
  }, []);
  return companyId;
}

export const useBuildingApartmentTypes = (buildingId: number) => {
  return useDataContainer(React.useCallback(() => {
    // return [];
    // return [{ apartment_type_id: 1 }, { apartment_type_id: 2 }, { apartment_type_id: 3 }];
    return buildingId ? getAppApartmentsTypesByBuilding(buildingId) : null;
  }, [buildingId]));
}

export const isOnlyFlatApartmentTypes = (apartmentsTypes: { apartment_type_id: number }[]) => {
  if (!apartmentsTypes) return true;
  return apartmentsTypes.length === 1 && apartmentsTypes[0].apartment_type_id === APARTMENT_TYPE.FLAT;
}

export const initBuildings = () => {
  userStore.subscribe((user) => {
    if (user.isAuth === false) {
      _buildingsLoaded = false;
      return;
    }
    loadBuildings();
  })
}
